<template>
  <div class="best-practices-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        Best practices automated
      </h2>
    </div>
    <div class="panel-content">
      <div
        v-if="loading"
        class="panel-loading"
      >
        <div
          v-for="i in 6"
          :key="i"
          class="skeleton-row"
        >
          <div class="skeleton-badge" />
          <div class="skeleton-lines">
            <div class="skeleton-line skeleton-line--medium" />
            <div class="skeleton-line skeleton-line--short" />
          </div>
        </div>
      </div>
      <div
        v-else-if="allPractices.length === 0"
        class="panel-empty"
      >
        No best practice data available
      </div>
      <div
        v-else
        class="practices-sections"
      >
        <div
          v-if="coveredPractices.length > 0"
          class="practices-section"
        >
          <h3 class="section-heading">
            Covered
          </h3>
          <div class="practices-list">
            <div
              v-for="practice in coveredPractices"
              :key="practice.type"
              class="practice-row clickable"
              @click="handleRowClick(practice)"
            >
              <span class="practice-badge practice-badge--green">
                <span class="material-icons-outlined">bolt</span>
              </span>
              <div class="practice-info">
                <span class="practice-label">{{ practice.label }}</span>
                <span class="practice-meta">{{ practice.statusText }}</span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="notCoveredPractices.length > 0"
          class="practices-section"
        >
          <h3 class="section-heading">
            Not covered
          </h3>
          <div class="practices-list">
            <div
              v-for="practice in notCoveredPractices"
              :key="practice.type"
              class="practice-row clickable"
              @click="handleRowClick(practice)"
            >
              <span
                class="practice-badge"
                :class="{
                  'practice-badge--yellow': practice.state === 'missed',
                  'practice-badge--grey': practice.state === 'noCoverage'
                }"
              >
                <span class="material-icons-outlined">bolt</span>
              </span>
              <div class="practice-info">
                <span class="practice-label">{{ practice.label }}</span>
                <span class="practice-meta">{{ practice.statusText }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useBestPracticesData } from '../composables/useBestPracticesData.js'
import { BEST_PRACTICE_COPY, formatBestPracticeCopy } from '../utils/widgetBestPractices.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'

defineEmits(['close'])

const { bestPractices, loading } = useBestPracticesData()

const allPractices = computed(() => {
  if (!bestPractices.value?.length) return []

  return bestPractices.value
    .filter(bp => BEST_PRACTICE_COPY[bp.type] && bp.type !== 'ROUTINE_COMMUNICATION')
    .map(bp => {
      const copy = BEST_PRACTICE_COPY[bp.type]
      let state = 'noCoverage'
      let statusText = copy?.noCoverage || 'Not automated'

      if (bp.is_covered) {
        state = 'covered'
        statusText = copy?.covered
          ? formatBestPracticeCopy(copy.covered, bp.proof_count, copy?.maxCount)
          : `${bp.proof_count || 0} actions completed`
      } else if (bp.missed_opportunity) {
        state = 'missed'
        statusText = copy?.missedShort
          ? formatBestPracticeCopy(copy.missedShort, bp.opportunity_count, copy?.maxCount)
          : 'Missed opportunity'
      }

      return {
        type: bp.type,
        label: copy?.label || bp.type,
        state,
        statusText,
        priority: bp.priority || 0,
        workflowId: bp.covering_workflow?.workflow_id || null,
        recipe: bp.covering_workflow_recipe || null
      }
    })
    .sort((a, b) => {
      // Covered first, then missed, then noCoverage
      const stateOrder = { covered: 0, missed: 1, noCoverage: 2 }
      const stateSort = (stateOrder[a.state] || 2) - (stateOrder[b.state] || 2)
      if (stateSort !== 0) return stateSort
      return b.priority - a.priority
    })
})

// Covered practices sorted by priority
const coveredPractices = computed(() => {
  return allPractices.value.filter(p => p.state === 'covered')
})

// Not covered: missed (purple) first, then noCoverage (grey), each sorted by priority
const notCoveredPractices = computed(() => {
  return allPractices.value
    .filter(p => p.state !== 'covered')
    .sort((a, b) => {
      const stateOrder = { missed: 0, noCoverage: 1 }
      const stateSort = (stateOrder[a.state] ?? 1) - (stateOrder[b.state] ?? 1)
      if (stateSort !== 0) return stateSort
      return b.priority - a.priority
    })
})

const handleRowClick = (practice) => {
  pushAdminSidePanel('widget-detail', {
    title: practice.label,
    bestPracticeType: practice.type
  })
}
</script>

<style scoped>
.best-practices-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 24px;
}

.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  padding: 24px 0;
}

.panel-loading {
  display: flex;
  flex-direction: column;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.skeleton-badge {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.skeleton-lines {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 4px;
  height: 14px;
}

.skeleton-line--medium { width: 50%; }
.skeleton-line--short { width: 70%; }

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Practice rows */
.practices-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.practices-section {
  display: flex;
  flex-direction: column;
}

.section-heading {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 4px 0;
  padding: 8px 0;
}

.practices-list {
  display: flex;
  flex-direction: column;
}

.practice-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.practice-row:last-child {
  border-bottom: none;
}

.practice-row.clickable {
  cursor: pointer;
}

.practice-row.clickable:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.practice-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
}

.practice-badge .material-icons-outlined {
  font-size: 16px;
}

.practice-badge--green {
  background: var(--color-bg-green);
}

.practice-badge--green .material-icons-outlined {
  color: #1C3829;
}

.practice-badge--yellow {
  background: var(--color-bg-yellow);
}

.practice-badge--yellow .material-icons-outlined {
  color: var(--color-yellow-text);
}

.practice-badge--grey {
  background: rgba(241, 240, 239, 1);
}

.practice-badge--grey .material-icons-outlined {
  color: #6b7280;
}

.practice-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.practice-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.practice-meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
